import { useState, useMemo, useEffect, useReducer } from 'react';
import { Box, Grid, Typography, Autocomplete, TextField, Button } from '@mui/material';
import Dropzone, { useDropzone } from 'react-dropzone';
import { useQuery, useMutation, gql, useLazyQuery } from '@apollo/client';
import { useSelector } from 'react-redux';

import { selectProject } from '../../features/project/projectSlice';
import PdfCard from '../common/PdfCard';
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from '../../utils/Constants';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { useAppDispatch } from '../../app/hooks';
import TextEditor from '../common/textEditor/TextEditor';
import LoadingWrapper from '../common/LoadingWrapper';
import { selectMerges } from '../../features/merge/mergeSlice';
import { IProjectSupplementalDocument } from '../../types/project';
import { IMerge } from '../../types/merge';
import { useMergeQuery } from '../../features/merge/mergeHooks';
import { supplementalDocumentsReducer } from '../../features/project/projectHooks';
import { FlexEnd } from '../../commonStyles';

const SupplementalDocuments = () => {
  const project = useSelector(selectProject);
  const storeDispatch = useAppDispatch();
  const [documents, setDocuments] = useState<ISupplementalDocument[]>([]);
  const [letter, setLetter] = useState<string>(project.letter);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [supplementalDocuments, supplementalDocumentsDispatch] = useReducer(supplementalDocumentsReducer, project.supplementals);
  const merges = useSelector(selectMerges);

  useMergeQuery(project._id, ['APS'], 0, 11);

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/gif, image/jpeg, image/png, application/pdf',
  });

  useEffect(() => {
    if (project.letter) {
      setLetter(project.letter);
    }
  }, [project]);

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const [updateProject] = useMutation(UPDATEPROJECT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Settings has been saved!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [sendLetterTest, { loading }] = useLazyQuery(SENDTEST, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Test Letter has been sent'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useQuery(DOCUMENTS, {
    variables: { project: project._id },
    onCompleted: (data) => {
      setDocuments(data.getS3Documents ? data.getS3Documents : []);
      let size =
        data.getS3Documents.reduce((a: any, b: any) => {
          return a + b.size;
        }, 0) / 1000000;
      setTotalSize(size);
    },
  });

  const [uploadDocuments] = useMutation(UPLOADDOCUMENTS, {
    variables: { project: project._id },
    onCompleted: (data) => {},
  });

  const [deleteDocument] = useMutation(DELETEDOCUMENT, {
    variables: { project: project._id },
    onCompleted: (data) => {},
  });

  const handleDrop = async (acceptedFiles: any) => {
    if (acceptedFiles.length === 0) {
      storeDispatch(showErrorSnackbar('This file type is not allowed'));
      return;
    }
    const files = acceptedFiles;
    let allFiles: any[] = [];
    if (files.length) {
      for (let i = 0; i < files.length; i++) {
        let filePromise = new Promise((resolve) => {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(files[i]);
          fileReader.onloadend = async () =>
            resolve({
              url: fileReader.result,
              name: files[i].name,
              file: files[i],
              size: files[i].size,
            });
        });
        allFiles.push(filePromise);
      }
    }

    Promise.all(allFiles).then((fileContents: any) => {
      if (fileContents[0].file.size / 1000000 + totalSize > 20) {
        return storeDispatch(showErrorSnackbar('Combined file size exceeds 20mb'));
      }
      uploadDocuments({
        variables: {
          project: project._id,
          documents: fileContents.map((fileContent: any) => {
            return {
              name: fileContent.name,
              file: fileContent.file,
            };
          }),
        },
      });
      storeDispatch(showSuccessSnackbar('Document(s) Uploaded!'));
      setTotalSize(fileContents[0].file.size / 1000000 + totalSize);
      setDocuments([...documents, ...fileContents]);
    });
  };

  const handleDelete = (id: string, name: string) => {
    deleteDocument({
      variables: {
        project: project._id,
        name: name,
      },
    }).then((res) => {
      storeDispatch(showSuccessSnackbar('Document Deleted!'));
      setDocuments(documents.filter((document: ISupplementalDocument) => document.name !== name));
    });
  };

  const sendTest = (content: string) => {
    sendLetterTest({
      variables: {
        project: project._id,
        type: 'letter',
        template: false,
      },
    });
  };

  const saveTemplate = (content: string) => {
    updateProject({
      variables: {
        _id: project?._id,
        record: {
          letter: content,
        },
      },
    });
  };

  const handleSupplementalDocuments = (id: string, value: any, name: string) => {
    if (supplementalDocuments.length === 0) {
      supplementalDocumentsDispatch({
        type: 'ADD',
        payload: { aps: null, documents: value.map((document: any) => document.name) },
      });
    } else {
      if (id === 'Default') {
        supplementalDocumentsDispatch({
          type: 'UPDATE',
          payload: { aps: null, documents: value.map((document: any) => document.name) },
        });
      } else {
        if (supplementalDocuments.some((document: any) => document.aps && document.aps._id === id)) {
          supplementalDocumentsDispatch({
            type: 'UPDATE',
            payload: { aps: { _id: id, name }, documents: value.map((document: any) => document.name) },
          });
        } else {
          supplementalDocumentsDispatch({
            type: 'ADD',
            payload: { aps: { _id: id, name }, documents: value.map((document: any) => document.name) },
          });
        }
      }
    }
  };

  const saveSupplementals = () => {
    updateProject({
      variables: {
        _id: project?._id,
        record: {
          supplementals: supplementalDocuments.map((supplemental: IProjectSupplementalDocument) => {
            return {
              aps: supplemental.aps ? supplemental.aps._id : null,
              documents: supplemental.documents,
            };
          }),
        },
      },
    });
  };

  const handleValue = (mergeName: string) => {
    if (mergeName === 'Default') {
      let selectedSupplemental = supplementalDocuments.find(
        (supplemental: IProjectSupplementalDocument) => supplemental.aps === null || supplemental.aps._id === null
      );
      if (selectedSupplemental) {
        return selectedSupplemental.documents.map((name: string) => {
          return {
            name: name,
            size: 0,
            url: '',
          };
        });
      } else return [];
    } else {
      let selectedSupplemental = supplementalDocuments.find(
        (supplemental: IProjectSupplementalDocument) => supplemental.aps && supplemental.aps.name === mergeName
      );
      if (selectedSupplemental) {
        return selectedSupplemental.documents.map((name: string) => {
          return {
            name: name,
            size: 0,
            url: '',
          };
        });
      } else return [];
    }
  };

  return (
    <Box sx={{ pb: 2 }}>
      {loading ? (
        <LoadingWrapper title="Sending Test..." modal={false} />
      ) : (
        <>
          <h2>Supplemental Documents</h2>
          <Box sx={{ mb: 2 }}>
            <strong>
              Please note that the total file size of all documents should be a maximum of 20mb. Current Total Size: {totalSize.toFixed(2)}
              mb
            </strong>
          </Box>
          <Box
            sx={{
              border: '1px solid #000',
              padding: '30px 20px',
              position: 'relative',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <Dropzone
              onDrop={(files) => (totalSize > 20 ? null : handleDrop(files))}
              accept="image/gif, image/jpg, image/jpeg, image/png, application/pdf"
            >
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps({ style })}>
                    <input {...getInputProps()} />
                    <p>Drag and Drop or Upload Images</p>
                  </div>
                </section>
              )}
            </Dropzone>
            <Grid container spacing={2}>
              {documents.map((document: ISupplementalDocument, index: number) => {
                return (
                  <Grid item xs={12} sm={6} md={4}>
                    <PdfCard title={document.name} file={document.url} id={index} handleDelete={handleDelete} download={true} />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
          <h2>Custom Letter</h2>
          <Box
            sx={{
              border: '1px solid #000',
              padding: '30px 20px',
              position: 'relative',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <TextEditor
              initContent={letter}
              index={0}
              renderButtons={[
                { name: 'Save', function: saveTemplate },
                { name: 'Send Test', function: sendTest },
              ]}
            />
          </Box>
          <h2>Custom Attachments for APS</h2>
          <Box
            sx={{
              border: '1px solid #000',
              padding: '30px 20px',
              position: 'relative',
              borderRadius: '8px',
              mb: 2,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ mb: 2 }}>
                <strong>Default</strong>
              </Typography>
              <Autocomplete
                sx={{ height: '100%' }}
                multiple
                options={documents}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option === value}
                disableClearable={false}
                freeSolo={false}
                value={handleValue('Default')}
                onChange={(e, value) => {
                  handleSupplementalDocuments('Default', value, 'Default');
                }}
                renderInput={(params) => <TextField {...params} label="Supplemental Documents" size="small" />}
              />
            </Box>
            {merges.map((merge: IMerge, index: number) => {
              return (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ mb: 2 }}>
                    <strong>{merge.name}</strong>
                  </Typography>
                  <Autocomplete
                    sx={{ height: '100%' }}
                    multiple
                    options={documents}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option === value}
                    disableClearable={false}
                    freeSolo={false}
                    value={handleValue(merge.name)}
                    onChange={(e, value) => {
                      handleSupplementalDocuments(merge._id!, value, merge.name);
                    }}
                    renderInput={(params) => <TextField {...params} label="Supplemental Documents" size="small" />}
                  />
                </Box>
              );
            })}
            <FlexEnd>
              <Button variant="contained" color="success" onClick={() => saveSupplementals()}>
                Save
              </Button>
            </FlexEnd>
          </Box>
        </>
      )}
    </Box>
  );
};

const DOCUMENTS = gql`
  query getS3Documents($project: MongoID!) {
    getS3Documents(project: $project) {
      name
      url
      size
    }
  }
`;

const UPLOADDOCUMENTS = gql`
  mutation uploadDocuments($project: MongoID!, $documents: [SupplementalDocument]) {
    uploadDocuments(project: $project, documents: $documents) {
      name
      url
    }
  }
`;

const DELETEDOCUMENT = gql`
  mutation deleteDocument($project: MongoID!, $name: String!) {
    deleteDocument(project: $project, name: $name)
  }
`;

const UPDATEPROJECT = gql`
  mutation projectUpdateById($_id: MongoID!, $record: UpdateByIdProjectInput!) {
    projectUpdateById(_id: $_id, record: $record) {
      record {
        _id
        letter
        supplementals {
          aps {
            _id
          }
          documents
        }
      }
    }
  }
`;

const SENDTEST = gql`
  query sendHtmlTest($project: MongoID!, $template: Boolean!, $type: String!) {
    sendHtmlTest(project: $project, template: $template, type: $type)
  }
`;

interface ISupplementalDocument {
  name: string;
  url: string;
  file?: any;
}

export default SupplementalDocuments;
